import * as React from "react";
import type { VizRenderPayload } from "@/lib/viz/types";
import { VegaLiteRenderer } from "./VegaLiteRenderer";
import { EChartsRenderer } from "./EChartsRenderer";
import { DeckGlRenderer } from "./DeckGlRenderer";
import { MermaidRenderer } from "./MermaidRenderer";

interface Props { payload: VizRenderPayload; }

/**
 * Dispatch a {@link VizRenderPayload} to the right format renderer.
 * Four formats wired: vega-lite, echarts, deck-gl, mermaid. Each
 * delegates to its own CDN-loaded library and surfaces a tabular /
 * raw-spec fallback inline when rendering fails (offline CDN, webgl
 * unsupported, malformed spec).
 */
export const VizRenderer: React.FC<Props> = ({ payload }) => {
  switch (payload.format) {
    case "vega-lite":
      return <VegaLiteRenderer payload={payload} />;
    case "echarts":
      return <EChartsRenderer payload={payload} />;
    case "deck-gl":
      return <DeckGlRenderer payload={payload} />;
    case "mermaid":
      return <MermaidRenderer payload={payload} />;
    default:
      // Unknown format token — should never happen given the backend's
      // enum-constrained output, but surface it explicitly rather than
      // silently dropping the chart.
      return (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <div className="font-semibold">Unsupported chart format</div>
          <div className="mt-1 text-xs">
            Got format=&quot;{(payload as { format?: string }).format ?? "(none)"}&quot;.
            The dataset has {payload.rows.length} rows.
          </div>
        </div>
      );
  }
};
